/**
 * PlanosPage — Editor de Planos Dual BuildPro
 * Modo 1: Pizarra vectorial nativa Excalidraw (offline, sin dependencias externas)
 * Modo 2: Autopilot IA para generación de presupuestos inteligentes via Groq
 */
'use client';

import React, { useState, useCallback, lazy, Suspense } from 'react';
import {
  Maximize2, Minimize2, Camera, Zap,
  HelpCircle, Ruler, Cuboid, PenTool, Loader2
} from 'lucide-react';
import { CadGenerator } from '../modules/buildpro/CadGenerator';
import '@excalidraw/excalidraw/index.css';

// Lazy-load Excalidraw to avoid SSR / CSP issues in Tauri production build
const Excalidraw = lazy(() =>
  import('@excalidraw/excalidraw').then(mod => ({ default: mod.Excalidraw }))
);

export function PlanosPage() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [activeMode, setActiveMode] = useState<'draw' | 'ai'>('draw');

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  const captureForScan = useCallback(() => {
    window.location.hash = '#/presupuestos-ia';
  }, []);

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-gray-950' : ''} flex flex-col`} style={{ height: isFullscreen ? '100vh' : 'calc(100vh - 120px)', minHeight: '600px' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-gray-950/95 backdrop-blur" style={{ flexShrink: 0 }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
            <Ruler size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-base font-bold text-white flex items-center gap-2">
              Editor de Planos
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-mono flex items-center gap-1"><Cuboid size={10} /> MCP gNucleus Activo</span>
            </h1>
            <p className="text-xs text-white/40">Vectorización Avanzada · Renderizado Text-To-CAD · Exportación BIM nativa</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-white/50 text-xs hover:text-white/80 hover:bg-white/5 transition-all"
            title="Ayuda rápida"
          >
            <HelpCircle size={13} />
            Guía
          </button>
          <button
            onClick={captureForScan}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500/20 to-amber-600/10 border border-amber-500/30 text-amber-300 text-xs font-semibold hover:bg-amber-500/25 transition-all"
            title="Ir a Presupuestos IA para escanear tu plano"
          >
            <Zap size={13} />
            Presupuestar con IA
          </button>
          <button
            onClick={() => {}}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-white/50 text-xs hover:text-white/80 hover:bg-white/5 transition-all"
            title="Importar archivo"
          >
            <Camera size={13} />
          </button>
          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-white/50 text-xs hover:text-white/80 hover:bg-white/5 transition-all"
            title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
          >
            {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
        </div>
      </div>

      {/* Quick Help Banner */}
      {showHelp && (
        <div className="px-5 py-3 bg-indigo-500/5 border-b border-indigo-500/20 text-xs text-white/60 space-y-1.5" style={{ flexShrink: 0 }}>
          <p className="text-indigo-300 font-semibold mb-1">🏗️ Guía rápida del Editor de Planos BuildPro</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-2 rounded-lg bg-white/5">
              <p className="text-white/80 font-medium">1. Dibujar</p>
              <p className="text-white/40">Usa las herramientas vectoriales para trazar muros, cotas y anotaciones</p>
            </div>
            <div className="p-2 rounded-lg bg-white/5">
              <p className="text-white/80 font-medium">2. Importar imagen</p>
              <p className="text-white/40">Arrastra un plano escaneado como fondo y dibuja encima</p>
            </div>
            <div className="p-2 rounded-lg bg-white/5">
              <p className="text-white/80 font-medium">3. Autopilot IA</p>
              <p className="text-white/40">Cambia a la pestaña IA y describe tu reforma para obtener un presupuesto</p>
            </div>
            <div className="p-2 rounded-lg bg-white/5">
              <p className="text-white/80 font-medium">4. Exportar</p>
              <p className="text-white/40">Guarda el plano como PNG/SVG o pásalo a Presupuestos IA</p>
            </div>
          </div>
          <button onClick={() => setShowHelp(false)} className="text-indigo-400 hover:text-indigo-300 underline mt-1">Cerrar guía</button>
        </div>
      )}

      {/* Mode Selector Tabs */}
      <div className="flex justify-center py-2 px-6 z-20 relative" style={{ flexShrink: 0 }}>
         <div className="flex bg-gray-900 border border-white/10 rounded-full p-1 shadow-2xl w-full max-w-md backdrop-blur">
            <button 
              onClick={() => setActiveMode('draw')} 
              className={`flex-1 py-1.5 px-4 rounded-full text-xs font-semibold flex items-center justify-center gap-2 transition-all ${activeMode === 'draw' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' : 'text-gray-400 hover:text-white'}`}
            >
              <PenTool size={14} /> Pizarra de Planos 2D
            </button>
            <button 
              onClick={() => setActiveMode('ai')} 
              className={`flex-1 py-1.5 px-4 rounded-full text-xs font-semibold flex items-center justify-center gap-2 transition-all ${activeMode === 'ai' ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20' : 'text-gray-400 hover:text-white'}`}
            >
              <Zap size={14} /> Autopilot Presupuestos
            </button>
         </div>
      </div>

      {/* Main Content Area — uses flex-1 + min-height 0 to fill remaining space */}
      <div style={{ flex: '1 1 0', minHeight: 0, position: 'relative', overflow: 'hidden' }}>
         {activeMode === 'draw' ? (
           <Suspense fallback={
             <div className="w-full h-full flex items-center justify-center bg-gray-950">
               <div className="text-center">
                 <Loader2 size={32} className="text-indigo-400 animate-spin mx-auto mb-3" />
                 <p className="text-white/40 text-sm">Cargando pizarra vectorial...</p>
               </div>
             </div>
           }>
             {/* Excalidraw needs a container with EXPLICIT dimensions — using absolute fill */}
             <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
               <Excalidraw 
                 theme="dark" 
                 UIOptions={{ canvasActions: { loadScene: false, export: { saveFileToDisk: true } } }} 
               />
             </div>
           </Suspense>
         ) : (
           <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'auto' }}>
             <div className="w-full h-full flex flex-col md:flex-row" style={{ minHeight: '100%' }}>
                <CadGenerator onClose={() => {}} contextPrompt="" inline />
             </div>
           </div>
         )}
      </div>
    </div>
  );
}

export default PlanosPage;
